import { useHead } from '@unhead/react'

import { getLocalizedPath, type RouteId } from '@/shared/routing/routePaths'
import { type Locale, useLocaleStore } from '@/shared/store/localeStore'

export interface SeoHeadProps {
  title: string
  description: string
  /**
   * Identificador lógico de la ruta. Cuando se pasa, `canonical` se deriva
   * automáticamente del `routePaths[routeId][currentLocale]` y se emiten
   * 3 `<link rel="alternate" hreflang="...">` (es, en, x-default). Preferido
   * para todas las rutas SSG-bilingües.
   */
  routeId?: RouteId
  /**
   * Path relativo o URL absoluta. Solo se usa cuando NO se pasa `routeId`
   * (rutas dinámicas tipo /sessions/:id que no tienen contraparte EN).
   */
  canonical?: string
  /** Path o URL de la imagen OG (1200×630). */
  ogImage?: string
  /** Si true, añade <meta name="robots" content="noindex, nofollow">. */
  noindex?: boolean
  /** Objeto(s) JSON-LD. Se emiten como <script type="application/ld+json">. */
  jsonLd?: object | object[]
}

const OG_LOCALE: Record<Locale, string> = {
  es: 'es_ES',
  en: 'en_US',
}

function abs(appUrl: string, pathOrUrl: string): string {
  return pathOrUrl.startsWith('http') ? pathOrUrl : `${appUrl}${pathOrUrl}`
}

/**
 * Inyecta meta SEO con {@link https://unhead.unjs.io @unhead/react}.
 *
 * Migrado desde {@code react-helmet-async} 2026-06-11 para resolver el bug
 * documentado en {@code vite.config.ts}: helmet sincronizaba el head vía
 * useEffect tras paint, fuera del snapshot de Puppeteer. Unhead se integra
 * con el plugin {@code @unhead/addons/vite} y captura el head en build-time
 * por cada ruta pre-renderizada — el {@code dist/[ruta]/index.html} sí lleva
 * title/canonical/OG/JSON-LD específicos.
 *
 * Uso preferido (rutas SSG-bilingües, con `routeId`):
 *   <SeoHead title="…" description="…" routeId="help-hub" ogImage="…" />
 *
 * Uso legacy (rutas dinámicas sin contraparte EN, p.ej. /sessions/:id):
 *   <SeoHead title="…" description="…" canonical="/sessions/42" />
 *
 * Con noindex (páginas auth):
 *   <SeoHead title="…" description="…" noindex />
 *
 * Con JSON-LD (landing):
 *   <SeoHead … jsonLd={[websiteSchema, orgSchema]} />
 */
export function SeoHead({
  title,
  description,
  routeId,
  canonical,
  ogImage,
  noindex = false,
  jsonLd,
}: SeoHeadProps) {
  const appUrl = (import.meta.env.VITE_APP_URL as string | undefined) ?? ''
  const locale = useLocaleStore((s) => s.locale)

  const canonicalUrl = routeId
    ? abs(appUrl, getLocalizedPath(routeId, locale))
    : canonical
      ? abs(appUrl, canonical)
      : undefined

  const alternates = routeId
    ? [
        { rel: 'alternate', hreflang: 'es', href: abs(appUrl, getLocalizedPath(routeId, 'es')) },
        { rel: 'alternate', hreflang: 'en', href: abs(appUrl, getLocalizedPath(routeId, 'en')) },
        {
          rel: 'alternate',
          hreflang: 'x-default',
          href: abs(appUrl, getLocalizedPath(routeId, 'es')),
        },
      ]
    : []

  const ogImageUrl = ogImage ? abs(appUrl, ogImage) : undefined

  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  // useHead acepta un objeto declarativo. Unhead dedupea por (tag, key
  // calculada) — para meta el "key" es name/property/http-equiv. Si en una
  // página dos SeoHead pintan og:title, gana el último que se renderice.
  // En este proyecto solo hay 1 SeoHead por ruta, así que no aplica.
  useHead({
    title,
    htmlAttrs: { lang: locale },
    meta: [
      { name: 'description', content: description },
      ...(noindex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),

      // Open Graph
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: OG_LOCALE[locale] },
      ...(canonicalUrl ? [{ property: 'og:url', content: canonicalUrl }] : []),
      ...(ogImageUrl
        ? [
            { property: 'og:image', content: ogImageUrl },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
          ]
        : []),

      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      ...(ogImageUrl ? [{ name: 'twitter:image', content: ogImageUrl }] : []),
    ],
    link: [...(canonicalUrl ? [{ rel: 'canonical', href: canonicalUrl }] : []), ...alternates],
    script: schemas.map((schema) => ({
      type: 'application/ld+json',
      // innerHTML es la forma de unhead de meter contenido inline en un <script>.
      // Los schemas llevan texto de usuario (título de la partida, nombre de
      // juego/tienda), así que `<` se escapa a `\u003c`: sigue siendo el mismo
      // JSON pero un "</script>" dentro de una string no puede cerrar el tag.
      innerHTML: JSON.stringify(schema).replace(/</g, '\\u003c'),
    })),
  })

  return null
}
